import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import classes from "./AddPatientPage.module.css";
import Header from "../../Components/Header/Header";
import Sidebar from "../../Components/Sidebar/Sidebar";
import ContactInfo from "./ContactInfo";
import MedicalInfo from "./MedicalInfo";
import {
  FaTimes,
  FaUserPlus,
  FaUser,
  FaPhone,
  FaHeartbeat,
  FaUsers,
  FaShieldAlt,
  FaFileAlt,
  FaUndo,
  FaArrowRight,
  FaArrowLeft,
  FaSave,
  FaUpload,
  FaTrash,
  FaFile,
  FaImage,
  FaFilePdf,
  FaFileWord,
} from "react-icons/fa";
import { BASE_URL } from "../../Components/API/Api";

// --- SKELETON LOADER COMPONENTS ---
const SkeletonField = () => (
  <div className={classes.skeletonField}>
    <div className={classes.skeletonLabel}></div>
    <div className={classes.skeletonInput}></div>
  </div>
);

const SkeletonGrid4 = () => (
  <div className={classes.grid4}>
    {Array(8).fill(0).map((_, i) => (
      <SkeletonField key={i} />
    ))}
  </div>
);

const SkeletonGrid3 = () => (
  <div className={classes.grid3}>
    {Array(3).fill(0).map((_, i) => (
      <SkeletonField key={i} />
    ))}
  </div>
);

const SkeletonSection = ({ title }) => (
  <div className={classes.skeletonSection}>
    <div className={classes.skeletonTitle}></div>
    <div className={classes.skeletonContent}>
      <SkeletonGrid4 />
    </div>
  </div>
);

const AddPatientPage = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const [activeTab, setActiveTab] = useState("personal");
  const [generatedUHID, setGeneratedUHID] = useState("");
  const [isGeneratingUHID, setIsGeneratingUHID] = useState(false);
  const [uhidError, setUhidError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // Document upload states
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccessMsg, setUploadSuccessMsg] = useState("");

  // Form state - Updated to match API with ALL required fields
  const [form, setForm] = useState({
    // Personal Fields
    first_name: "",
    middle_name: "",
    last_name: "",
    patient_no: "",
    date_of_birth: "",
    age: "",
    gender: "",
    blood_group: "",
    marital_status: "",
    nationality: "",
    religion: "",
    identification_type: "",
    identification_number: "",
    date_of_registration: "",
    referred_by: "",
    // Address Fields
    address: "",
    address_line_2: "",
    country: "",
    state: "",
    city: "",
    postal_code: "",
    // Primary Physician
    department: "",
    doctor: "",
    consultation_type: "",
    // Contact Information
    phone: "",
    email: "",
    smsNotifications: false,
    emailNotifications: false,
    appointmentReminders: false,
    // Medical Information
    genotype: "",
    height: "",
    weight: "",
    bmi: "",
    past_med_history: "",
    family_med_history: "",
    medical_conditions: [],
    allergies: [],
    allergyName: "",
    reaction: "",
    surgery_name: [],
    surgery_date: [],
    hospital_name: [],
    takingMedication: "No",
    medicationName: "",
    dosage: "",
    frequency: "",
    route: "",
    previousSurgery: "No",
    procedureName: "",
    surgeryDate: "",
    hospital: "",
    noKnownConditions: false,
    noKnownAllergies: false,
    // Emergency Contact
    emergency_contact_name: "",
    emergency_contact_relationship: "",
    emergency_contact_phone: "",
    // Insurance
    insurance_id: "",
    // Status
    status: "Active",
    company_id: "",
  });

  // Generate UHID on component mount
  useEffect(() => {
    const init = async () => {
      await generateUHID();
      setIsLoading(false);
    };
    init();
  }, []);

  // Function to generate UHID from API using GET method
  const generateUHID = async () => {
    setIsGeneratingUHID(true);
    setUhidError("");
    try {
      const companyId = localStorage.getItem("companyId") || "1";
      
      const response = await axios.get(
        `${BASE_URL}/api/patient/generate_number`,
        {
          params: {
            company_id: parseInt(companyId),
          },
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          },
        }
      );

      console.log("UHID Response:", response.data);

      let uhid = "";
      if (response.data && response.data.data) {
        uhid = response.data.data.uhid || 
               response.data.data.patient_number || 
               response.data.data.number ||
               response.data.data;
      } else if (response.data && response.data.uhid) {
        uhid = response.data.uhid;
      } else if (response.data && response.data.number) {
        uhid = response.data.number;
      } else if (typeof response.data === "string") {
        uhid = response.data;
      } else if (response.data && response.data.data && typeof response.data.data === "string") {
        uhid = response.data.data;
      }

      if (uhid) {
        setGeneratedUHID(uhid);
        setForm((prev) => ({ ...prev, patient_no: uhid }));
      } else {
        setUhidError("Failed to generate UHID. Please try again.");
        const fallbackUhid = `UHID${Date.now().toString().slice(-8)}`;
        setGeneratedUHID(fallbackUhid);
        setForm((prev) => ({ ...prev, patient_no: fallbackUhid }));
      }
    } catch (error) {
      console.error("Error generating UHID:", error);
      
      let errorMsg = "Failed to generate UHID. Please try again.";
      if (error.response) {
        if (error.response.status === 401) {
          errorMsg = "Unauthorized. Please login again.";
        } else if (error.response.data?.message) {
          errorMsg = error.response.data.message;
        }
      } else if (error.request) {
        errorMsg = "Unable to connect to server. Please check your internet connection.";
      }
      
      setUhidError(errorMsg);
      const fallbackUhid = `UHID${Date.now().toString().slice(-8)}`;
      setGeneratedUHID(fallbackUhid);
      setForm((prev) => ({ ...prev, patient_no: fallbackUhid }));
    } finally {
      setIsGeneratingUHID(false);
    }
  };

  // Calculate BMI
  const calculateBMI = () => {
    const weight = parseFloat(form.weight);
    const height = parseFloat(form.height);

    if (!weight || !height) return "";

    // Height is now in cm, convert to meters for BMI calculation
    const heightInMeters = height / 100;
    const bmi = weight / (heightInMeters * heightInMeters);
    return bmi.toFixed(1);
  };

  // --- VALIDATION FUNCTIONS ---

  // Validate Personal Information tab
  const validatePersonalTab = () => {
    const errors = [];
    
    if (!form.first_name || form.first_name.trim() === "") {
      errors.push("First Name is required");
    }
    if (!form.last_name || form.last_name.trim() === "") {
      errors.push("Last Name is required");
    }
    if (!form.date_of_birth) {
      errors.push("Date of Birth is required");
    }
    if (!form.gender) {
      errors.push("Gender is required");
    }
    if (!form.patient_no && !generatedUHID) {
      errors.push("UHID is required");
    }

    return errors;
  };

  // Validate Contact Information tab
  const validateContactTab = () => {
    const errors = [];
    
    if (!form.phone || form.phone.trim() === "") {
      errors.push("Primary Phone Number is required");
    } else if (!/^[0-9+\-\s()]{10,15}$/.test(form.phone)) {
      errors.push("Please enter a valid phone number");
    }
    if (!form.email || form.email.trim() === "") {
      errors.push("Email Address is required");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errors.push("Please enter a valid email address");
    }

    return errors;
  };

  // Validate Medical Information tab
  const validateMedicalTab = () => {
    const errors = [];
    
    if (!form.blood_group) {
      errors.push("Blood Group is required");
    }
    if (!form.genotype) {
      errors.push("Genotype is required");
    }

    return errors;
  };

  // Validate Emergency Contact tab
  const validateEmergencyTab = () => {
    const errors = [];
    
    if (!form.emergency_contact_name || form.emergency_contact_name.trim() === "") {
      errors.push("Emergency Contact Name is required");
    }
    if (!form.emergency_contact_relationship || form.emergency_contact_relationship.trim() === "") {
      errors.push("Emergency Contact Relationship is required");
    }
    if (!form.emergency_contact_phone || form.emergency_contact_phone.trim() === "") {
      errors.push("Emergency Contact Phone is required");
    } else if (!/^[0-9+\-\s()]{10,15}$/.test(form.emergency_contact_phone)) {
      errors.push("Please enter a valid emergency contact phone number");
    }

    return errors;
  };

  // Validate Insurance tab
  const validateInsuranceTab = () => {
    const errors = [];
    return errors;
  };

  // Validate Documents tab
  const validateDocumentsTab = () => {
    const errors = [];
    return errors;
  };

  // Navigate to next tab with validation
  const goToNextTab = (currentTab) => {
    let errors = [];

    switch (currentTab) {
      case "personal":
        errors = validatePersonalTab();
        break;
      case "contact":
        errors = validateContactTab();
        break;
      case "medical":
        errors = validateMedicalTab();
        break;
      case "emergency":
        errors = validateEmergencyTab();
        break;
      case "insurance":
        errors = validateInsuranceTab();
        break;
      case "documents":
        errors = validateDocumentsTab();
        break;
      default:
        break;
    }

    if (errors.length > 0) {
      const errorMessage = errors.map((err, index) => `${index + 1}. ${err}`).join('\n');
      Swal.fire({
        icon: 'warning',
        title: 'Please fix the following errors',
        html: `<div style="text-align: left; font-size: 14px;">${errorMessage}</div>`,
        confirmButtonColor: '#2F80ED',
        confirmButtonText: 'OK',
      });
      return false;
    }

    const tabKeys = ["personal", "contact", "medical", "emergency", "insurance", "documents"];
    const currentIndex = tabKeys.indexOf(currentTab);
    if (currentIndex < tabKeys.length - 1) {
      setActiveTab(tabKeys[currentIndex + 1]);
    }
    return true;
  };

  // Navigate to previous tab
  const goToPreviousTab = (currentTab) => {
    const tabKeys = ["personal", "contact", "medical", "emergency", "insurance", "documents"];
    const currentIndex = tabKeys.indexOf(currentTab);
    if (currentIndex > 0) {
      setActiveTab(tabKeys[currentIndex - 1]);
    }
  };

  // Handle all types of inputs (text, select, checkbox)
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setSubmitError("");
    setSubmitSuccess("");
    
    if (name === "height" || name === "weight") {
      const bmi = calculateBMI();
      if (bmi) {
        setForm((prev) => ({ ...prev, bmi: bmi }));
      }
    }
  };

  // Handle medical conditions as comma-separated string to array
  const handleMedicalConditionsChange = (e) => {
    const value = e.target.value;
    const conditionsArray = value ? value.split(',').map(item => item.trim()).filter(Boolean) : [];
    setForm((prev) => ({
      ...prev,
      medical_conditions: conditionsArray
    }));
    setSubmitError("");
    setSubmitSuccess("");
  };

  // Handle allergies as comma-separated string to array
  const handleAllergiesChange = (e) => {
    const value = e.target.value;
    const allergiesArray = value ? value.split(',').map(item => item.trim()).filter(Boolean) : [];
    setForm((prev) => ({
      ...prev,
      allergies: allergiesArray
    }));
    setSubmitError("");
    setSubmitSuccess("");
  };

  // Handle surgeries as comma-separated string to array
  const handleSurgeryChange = (e) => {
    const { name, value } = e.target;
    const arrayValue = value ? value.split(',').map(item => item.trim()).filter(Boolean) : [];
    setForm((prev) => ({
      ...prev,
      [name]: arrayValue
    }));
    setSubmitError("");
    setSubmitSuccess("");
  };

  // Document upload form state
  const [documentForm, setDocumentForm] = useState({
    document_type: "",
    document_name: "",
    documents: [],
  });

  const documentTypeOptions = [
    "Medical Report",
    "Prescription",
    "Lab Result",
    "X-Ray",
    "MRI",
    "CT Scan",
    "Ultrasound",
    "ECG",
    "Medical Certificate",
    "Insurance Document",
    "ID Proof",
    "Consent Form",
    "Discharge Summary",
    "Referral Letter",
    "Other",
  ];

  const handleDocumentChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file") {
      const fileArray = Array.from(files);
      setDocumentForm((prev) => ({
        ...prev,
        documents: fileArray,
      }));
    } else {
      setDocumentForm((prev) => ({ ...prev, [name]: value }));
    }
    setUploadError("");
    setUploadSuccessMsg("");
  };

  const handleRemoveDocument = (index) => {
    setDocumentForm((prev) => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index),
    }));
  };

  const handleUploadDocuments = async () => {
    setUploadError("");
    setUploadSuccessMsg("");
    
    if (!documentForm.document_type) {
      setUploadError("Please select a document type.");
      return;
    }
    if (!documentForm.document_name) {
      setUploadError("Please enter a document name.");
      return;
    }
    if (documentForm.documents.length === 0) {
      setUploadError("Please select at least one file to upload.");
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('document_type', documentForm.document_type);
      formData.append('document_name', documentForm.document_name);
      
      documentForm.documents.forEach((file) => {
        formData.append('documents[]', file);
      });

      const response = await axios.post(
        `${BASE_URL}/api/documents/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          },
        }
      );

      console.log("Upload Response:", response.data);

      const newDocuments = response.data.data || [];
      setUploadedDocuments((prev) => [...prev, ...(Array.isArray(newDocuments) ? newDocuments : [newDocuments])]);
      
      setUploadSuccessMsg("Documents uploaded successfully!");
      
      setDocumentForm({
        document_type: "",
        document_name: "",
        documents: [],
      });

    } catch (error) {
      console.error("Error uploading documents:", error);
      let errorMsg = "Failed to upload documents. Please try again.";
      if (error.response) {
        if (error.response.status === 401) {
          errorMsg = "Unauthorized. Please login again.";
        } else if (error.response.data?.message) {
          errorMsg = error.response.data.message;
        } else if (error.response.data?.errors) {
          const errors = error.response.data.errors;
          const firstError = Object.keys(errors)[0];
          if (firstError && errors[firstError][0]) {
            errorMsg = errors[firstError][0];
          }
        }
      } else if (error.request) {
        errorMsg = "Unable to connect to server. Please check your internet connection.";
      }
      setUploadError(errorMsg);
    } finally {
      setIsUploading(false);
    }
  };

  const handleReset = () => {
    setForm({
      first_name: "",
      middle_name: "",
      last_name: "",
      patient_no: generatedUHID || "",
      date_of_birth: "",
      age: "",
      gender: "",
      blood_group: "",
      marital_status: "",
      nationality: "",
      religion: "",
      identification_type: "",
      identification_number: "",
      date_of_registration: "",
      referred_by: "",
      address: "",
      address_line_2: "",
      country: "",
      state: "",
      city: "",
      postal_code: "",
      department: "",
      doctor: "",
      consultation_type: "",
      phone: "",
      email: "",
      smsNotifications: false,
      emailNotifications: false,
      appointmentReminders: false,
      genotype: "",
      height: "",
      weight: "",
      bmi: "",
      past_med_history: "",
      family_med_history: "",
      medical_conditions: [],
      allergies: [],
      allergyName: "",
      reaction: "",
      surgery_name: [],
      surgery_date: [],
      hospital_name: [],
      takingMedication: "No",
      medicationName: "",
      dosage: "",
      frequency: "",
      route: "",
      previousSurgery: "No",
      procedureName: "",
      surgeryDate: "",
      hospital: "",
      noKnownConditions: false,
      noKnownAllergies: false,
      emergency_contact_name: "",
      emergency_contact_relationship: "",
      emergency_contact_phone: "",
      insurance_id: "",
      status: "Active",
      company_id: "",
    });
    setUploadedDocuments([]);
    setDocumentForm({
      document_type: "",
      document_name: "",
      documents: [],
    });
    setSubmitError("");
    setSubmitSuccess("");
    generateUHID();
  };

  // Handle Create Patient with SweetAlert
  const handleCreatePatient = async () => {
    setSubmitError("");
    setSubmitSuccess("");
    setIsSubmitting(true);

    // Validate required fields
    if (!form.first_name || !form.last_name || !form.date_of_birth || !form.gender) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please fill in all required fields (First Name, Last Name, Date of Birth, Gender)',
        confirmButtonColor: '#2F80ED',
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const companyId = localStorage.getItem("companyId") || "1";
      
      const bmiValue = calculateBMI();
      
      // Prepare document arrays for API
      const documentNamesArray = uploadedDocuments.map(doc => doc.document_name);
      const documentTypesArray = uploadedDocuments.map(doc => doc.document_type);
      
      const payload = {
        company_id: parseInt(companyId),
        patient_no: form.patient_no || generatedUHID,
        first_name: form.first_name,
        middle_name: form.middle_name || null,
        last_name: form.last_name,
        gender: form.gender || "Male",
        date_of_birth: form.date_of_birth,
        phone: form.phone || null,
        email: form.email || null,
        address: form.address || null,
        blood_group: form.blood_group || null,
        genotype: form.genotype || null,
        marital_status: form.marital_status || null,
        occupation: form.occupation || null,
        emergency_contact_name: form.emergency_contact_name || null,
        emergency_contact_relationship: form.emergency_contact_relationship || null,
        emergency_contact_phone: form.emergency_contact_phone || null,
        insurance_id: form.insurance_id ? parseInt(form.insurance_id) : null,
        status: form.status || "Active",
        age: form.age || null,
        nationality: form.nationality || null,
        religion: form.religion || null,
        identification_type: form.identification_type || null,
        identification_number: form.identification_number || null,
        date_of_registration: form.date_of_registration || null,
        referred_by: form.referred_by || null,
        address_line_2: form.address_line_2 || null,
        country: form.country || null,
        state: form.state || null,
        city: form.city || null,
        postal_code: form.postal_code || null,
        department: form.department || null,
        doctor: form.doctor || null,
        consultation_type: form.consultation_type || null,
        past_med_history: form.past_med_history || null,
        family_med_history: form.family_med_history || null,
        height: form.height || null,
        weight: form.weight || null,
        bmi: bmiValue || null,
        medical_conditions: form.medical_conditions.length > 0 ? form.medical_conditions : null,
        allergies: form.allergies.length > 0 ? form.allergies : null,
        surgery_name: form.surgery_name.length > 0 ? form.surgery_name : null,
        surgery_date: form.surgery_date.length > 0 ? form.surgery_date : null,
        hospital_name: form.hospital_name.length > 0 ? form.hospital_name : null,
        document_name: documentNamesArray.length > 0 ? documentNamesArray : null,
        document_type: documentTypesArray.length > 0 ? documentTypesArray : null,
      };

      console.log("Creating Patient with payload:", payload);

      const response = await axios.post(
        `${BASE_URL}/api/patient/create`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          },
        }
      );

      console.log("Patient Created:", response.data);

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Patient created successfully!',
        confirmButtonColor: '#2F80ED',
        timer: 2000,
        timerProgressBar: true,
      });

      setSubmitSuccess("Patient created successfully!");
      
      generateUHID();
      
      setTimeout(() => {
        handleReset();
        setTimeout(() => {
          navigate("/patient");
        }, 1000);
      }, 1000);

    } catch (error) {
      console.error("Error creating patient:", error);
      
      let errorMsg = "Failed to create patient. Please try again.";
      if (error.response) {
        if (error.response.status === 401) {
          errorMsg = "Unauthorized. Please login again.";
        } else if (error.response.data?.message) {
          const message = error.response.data.message;
          if (typeof message === 'object') {
            const firstKey = Object.keys(message)[0];
            if (firstKey && Array.isArray(message[firstKey])) {
              errorMsg = message[firstKey][0];
            } else {
              errorMsg = JSON.stringify(message);
            }
          } else if (typeof message === 'string') {
            errorMsg = message;
          }
        } else if (error.response.data?.data) {
          const apiErrors = error.response.data.data;
          if (typeof apiErrors === "object") {
            const firstKey = Object.keys(apiErrors)[0];
            if (firstKey && Array.isArray(apiErrors[firstKey])) {
              errorMsg = apiErrors[firstKey][0];
            }
          }
        }
      } else if (error.request) {
        errorMsg = "Unable to connect to server. Please check your internet connection.";
      }
      
      setSubmitError(errorMsg);
      
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: errorMsg,
        confirmButtonColor: '#2F80ED',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const tabs = [
    { key: "personal", label: "Personal Information", icon: <FaUser /> },
    { key: "contact", label: "Contact Information", icon: <FaPhone /> },
    { key: "medical", label: "Medical Information", icon: <FaHeartbeat /> },
    { key: "emergency", label: "Emergency Contact", icon: <FaUsers /> },
    { key: "insurance", label: "Insurance Information", icon: <FaShieldAlt /> },
    { key: "documents", label: "Documents", icon: <FaFileAlt /> },
  ];

  // Get file icon based on file type
  const getFileIcon = (fileName) => {
    if (!fileName) return <FaFile />;
    const ext = fileName.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) {
      return <FaImage />;
    }
    if (['pdf'].includes(ext)) {
      return <FaFilePdf />;
    }
    if (['doc', 'docx', 'txt', 'rtf'].includes(ext)) {
      return <FaFileWord />;
    }
    return <FaFile />;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Render skeleton loader for personal tab
  const renderPersonalSkeleton = () => (
    <div className={classes.skeletonContainer}>
      <SkeletonSection title="Personal Details" />
      <SkeletonSection title="Address Information" />
      <div className={classes.skeletonSection}>
        <div className={classes.skeletonTitle}></div>
        <div className={classes.skeletonContent}>
          <SkeletonGrid3 />
        </div>
      </div>
      <div className={classes.skeletonFooter}>
        <div className={classes.skeletonButton}></div>
        <div className={classes.skeletonButton}></div>
      </div>
    </div>
  );

  // Show skeleton while loading
  if (isLoading) {
    return (
      <div className={classes.pageContainer}>
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <div className={classes.mainArea}>
          <Header toggleSidebar={toggleSidebar} />
          <div className={classes.content}>
            <div className={classes.topHeader}>
              <div className={classes.headerLeft}>
                <div className={classes.headerIcon}>
                  <FaUserPlus />
                </div>
                <div>
                  <h2>Add New Patient</h2>
                  <p>Fill in the details below to register a new patient</p>
                </div>
              </div>
            </div>
            <div className={classes.tabs}>
              {tabs.map((tab) => (
                <button key={tab.key} className={classes.tab}>
                  {tab.icon} {tab.label}
                </button>
              ))}
            </div>
            <div className={classes.body}>
              {renderPersonalSkeleton()}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.pageContainer}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <div className={classes.mainArea}>
        <Header toggleSidebar={toggleSidebar} />

        <div className={classes.content}>
          {/* --- Top Header --- */}
          <div className={classes.topHeader}>
            <div className={classes.headerLeft}>
              <div className={classes.headerIcon}>
                <FaUserPlus />
              </div>
              <div>
                <h2>Add New Patient</h2>
                <p>Fill in the details below to register a new patient</p>
              </div>
            </div>
            <button
              className={classes.closeBtn}
              onClick={() => navigate("/patient")}
            >
              <FaTimes />
            </button>
          </div>

          {/* --- Tabs --- */}
          <div className={classes.tabs}>
            {tabs.map((tab) => (
              <button
                key={tab.key}
                className={`${classes.tab} ${activeTab === tab.key ? classes.activeTab : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          {/* --- Body --- */}
          <div className={classes.body}>
            
            {/* Submit Success/Error Messages */}
            {submitSuccess && (
              <div className={classes.successMessage}>
                <span>{submitSuccess}</span>
              </div>
            )}
            {submitError && (
              <div className={classes.submitError}>
                <span>{submitError}</span>
              </div>
            )}

            {/* Render based on activeTab */}
            {activeTab === "personal" && (
              <>
                {/* PERSONAL DETAILS */}
                <h3>Personal Details</h3>

                <div className={classes.grid4}>
                  <div>
                    <label>First Name <span className={classes.required}>*</span></label>
                    <input 
                      name="first_name" 
                      value={form.first_name} 
                      onChange={handleChange} 
                      placeholder="Enter first name"
                      className={classes.formInput}
                    />
                  </div>
                  <div>
                    <label>Middle Name</label>
                    <input 
                      name="middle_name" 
                      value={form.middle_name} 
                      onChange={handleChange} 
                      placeholder="Enter middle name"
                      className={classes.formInput}
                    />
                  </div>
                  <div>
                    <label>Last Name <span className={classes.required}>*</span></label>
                    <input 
                      name="last_name" 
                      value={form.last_name} 
                      onChange={handleChange} 
                      placeholder="Enter last name"
                      className={classes.formInput}
                    />
                  </div>
                  <div>
                    <label>UHID / MRN <span className={classes.required}>*</span></label>
                    <div className={classes.uhidWrapper}>
                      <input 
                        placeholder={isGeneratingUHID ? "Generating..." : "Auto generated"} 
                        value={form.patient_no || generatedUHID || ""}
                        readOnly
                        className={classes.uhidInput}
                      />
                      <button 
                        type="button" 
                        className={classes.regenerateBtn}
                        onClick={generateUHID}
                        disabled={isGeneratingUHID}
                        title="Regenerate UHID"
                      >
                        {isGeneratingUHID ? "⏳" : "⟳"}
                      </button>
                    </div>
                    {uhidError && <span className={classes.uhidError}>{uhidError}</span>}
                  </div>
                  <div>
                    <label>Date of Birth <span className={classes.required}>*</span></label>
                    <input 
                      type="date" 
                      name="date_of_birth" 
                      value={form.date_of_birth} 
                      onChange={handleChange} 
                      className={classes.formInput}
                    />
                  </div>
                  <div>
                    <label>Age</label>
                    <input 
                      name="age" 
                      value={form.age} 
                      onChange={handleChange} 
                      placeholder="Enter age"
                      className={classes.formInput}
                    />
                  </div>
                  <div>
                    <label>Gender <span className={classes.required}>*</span></label>
                    <select name="gender" value={form.gender} onChange={handleChange} className={classes.formSelect}>
                      <option value="">Select gender</option>
                      <option>Male</option>
                      <option>Female</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div>
                    <label>Blood Group</label>
                    <select name="blood_group" value={form.blood_group} onChange={handleChange} className={classes.formSelect}>
                      <option value="">Select blood group</option>
                      <option>A+</option>
                      <option>A-</option>
                      <option>B+</option>
                      <option>B-</option>
                      <option>AB+</option>
                      <option>AB-</option>
                      <option>O+</option>
                      <option>O-</option>
                    </select>
                  </div>
                  <div>
                    <label>Marital Status</label>
                    <select name="marital_status" value={form.marital_status} onChange={handleChange} className={classes.formSelect}>
                      <option value="">Select marital status</option>
                      <option>Single</option>
                      <option>Married</option>
                      <option>Divorced</option>
                      <option>Widowed</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div>
                    <label>Nationality</label>
                    <input 
                      name="nationality" 
                      value={form.nationality} 
                      onChange={handleChange} 
                      placeholder="Enter nationality"
                      className={classes.formInput}
                    />
                  </div>
                  <div>
                    <label>Religion</label>
                    <input 
                      name="religion" 
                      value={form.religion} 
                      onChange={handleChange} 
                      placeholder="Enter religion"
                      className={classes.formInput}
                    />
                  </div>
                  <div>
                    <label>Occupation</label>
                    <input 
                      name="occupation" 
                      value={form.occupation} 
                      onChange={handleChange} 
                      placeholder="Enter occupation"
                      className={classes.formInput}
                    />
                  </div>
                  <div>
                    <label>Identification Type</label>
                    <select name="identification_type" value={form.identification_type} onChange={handleChange} className={classes.formSelect}>
                      <option value="">Select ID Type</option>
                      <option>NIN</option>
                      <option>Passport</option>
                      <option>Driver's License</option>
                      <option>Voter's Card</option>
                    </select>
                  </div>
                  <div>
                    <label>Identification Number</label>
                    <input 
                      name="identification_number" 
                      value={form.identification_number} 
                      onChange={handleChange} 
                      placeholder="Enter ID number"
                      className={classes.formInput}
                    />
                  </div>
                  <div>
                    <label>Date of Registration</label>
                    <input 
                      type="date" 
                      name="date_of_registration" 
                      value={form.date_of_registration} 
                      onChange={handleChange} 
                      className={classes.formInput}
                    />
                  </div>
                  <div>
                    <label>Referred By</label>
                    <input 
                      name="referred_by" 
                      value={form.referred_by} 
                      onChange={handleChange} 
                      placeholder="Enter referral source"
                      className={classes.formInput}
                    />
                  </div>
                </div>

                {/* ADDRESS */}
                <h3>Address Information</h3>

                <div className={classes.grid4}>
                  <div className={classes.full}>
                    <label>Address Line 1</label>
                    <input 
                      name="address" 
                      value={form.address} 
                      onChange={handleChange} 
                      placeholder="Enter address line 1"
                      className={classes.formInput}
                    />
                  </div>
                  <div className={classes.full}>
                    <label>Address Line 2</label>
                    <input 
                      name="address_line_2" 
                      value={form.address_line_2} 
                      onChange={handleChange} 
                      placeholder="Enter address line 2"
                      className={classes.formInput}
                    />
                  </div>
                  <div>
                    <label>Country</label>
                    <input 
                      name="country" 
                      value={form.country} 
                      onChange={handleChange} 
                      placeholder="Enter country"
                      className={classes.formInput}
                    />
                  </div>
                  <div>
                    <label>State</label>
                    <input 
                      name="state" 
                      value={form.state} 
                      onChange={handleChange} 
                      placeholder="Enter state"
                      className={classes.formInput}
                    />
                  </div>
                  <div>
                    <label>City</label>
                    <input 
                      name="city" 
                      value={form.city} 
                      onChange={handleChange} 
                      placeholder="Enter city"
                      className={classes.formInput}
                    />
                  </div>
                  <div>
                    <label>Postal Code</label>
                    <input 
                      name="postal_code" 
                      value={form.postal_code} 
                      onChange={handleChange} 
                      placeholder="Enter postal code"
                      className={classes.formInput}
                    />
                  </div>
                </div>

                {/* PRIMARY PHYSICIAN */}
                <h3>Primary Physician</h3>

                <div className={classes.grid3}>
                  <div>
                    <label>Department</label>
                    <input 
                      name="department" 
                      value={form.department} 
                      onChange={handleChange} 
                      placeholder="Enter department"
                      className={classes.formInput}
                    />
                  </div>
                  <div>
                    <label>Doctor</label>
                    <input 
                      name="doctor" 
                      value={form.doctor} 
                      onChange={handleChange} 
                      placeholder="Enter doctor name"
                      className={classes.formInput}
                    />
                  </div>
                  <div>
                    <label>Consultation Type</label>
                    <input 
                      name="consultation_type" 
                      value={form.consultation_type} 
                      onChange={handleChange} 
                      placeholder="Enter consultation type"
                      className={classes.formInput}
                    />
                  </div>
                </div>

                {/* Footer for Personal Info */}
                <div className={classes.footer}>
                  <div className={classes.note}>
                    After saving, you can add more details, documents and medical history from the patient profile.
                  </div>
                  <div className={classes.footerButtons}>
                    <button className={classes.reset} onClick={handleReset} type="button" disabled={isSubmitting}>
                      <FaUndo /> Reset
                    </button>
                    <button 
                      className={classes.next} 
                      onClick={() => goToNextTab("personal")}
                      disabled={isSubmitting}
                    >
                      Next <FaArrowRight />
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* CONTACT INFORMATION TAB */}
            {activeTab === "contact" && (
              <ContactInfo
                formData={form}
                setFormData={setForm}
                onPrevious={() => goToPreviousTab("contact")}
                onNext={() => goToNextTab("contact")}
              />
            )}

            {/* MEDICAL INFORMATION TAB */}
            {activeTab === "medical" && (
              <MedicalInfo
                formData={form}
                setFormData={setForm}
                onPrevious={() => goToPreviousTab("medical")}
                onNext={() => goToNextTab("medical")}
              />
            )}

            {/* EMERGENCY CONTACT TAB */}
            {activeTab === "emergency" && (
              <div>
                <h3>Emergency Contact Information</h3>
                <p className={classes.docSubtitle}>Please provide emergency contact information for the patient.</p>
                
                <div className={classes.grid3}>
                  <div>
                    <label>Emergency Contact Name <span className={classes.required}>*</span></label>
                    <input 
                      name="emergency_contact_name" 
                      value={form.emergency_contact_name} 
                      onChange={handleChange} 
                      placeholder="Enter emergency contact name"
                      disabled={isSubmitting}
                      className={classes.formInput}
                    />
                  </div>
                  <div>
                    <label>Relationship <span className={classes.required}>*</span></label>
                    <input 
                      name="emergency_contact_relationship" 
                      value={form.emergency_contact_relationship} 
                      onChange={handleChange} 
                      placeholder="e.g., Spouse, Parent, Sibling"
                      disabled={isSubmitting}
                      className={classes.formInput}
                    />
                  </div>
                  <div>
                    <label>Emergency Contact Phone <span className={classes.required}>*</span></label>
                    <input 
                      name="emergency_contact_phone" 
                      value={form.emergency_contact_phone} 
                      onChange={handleChange} 
                      placeholder="Enter phone number"
                      disabled={isSubmitting}
                      className={classes.formInput}
                    />
                  </div>
                </div>

                <div className={classes.footer}>
                  <div className={classes.note}>
                    This information will be used in case of emergency.
                  </div>
                  <div className={classes.footerButtons}>
                    <button 
                      className={classes.reset} 
                      onClick={() => goToPreviousTab("emergency")} 
                      disabled={isSubmitting}
                    >
                      <FaArrowLeft /> Previous
                    </button>
                    <button 
                      className={classes.next} 
                      onClick={() => goToNextTab("emergency")}
                      disabled={isSubmitting}
                    >
                      Next <FaArrowRight />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* INSURANCE TAB */}
            {activeTab === "insurance" && (
              <div>
                <h3>Insurance Information</h3>
                <p className={classes.docSubtitle}>Please provide insurance information if applicable.</p>
                
                <div className={classes.grid3}>
                  <div>
                    <label>Insurance ID</label>
                    <input 
                      name="insurance_id" 
                      value={form.insurance_id} 
                      onChange={handleChange} 
                      placeholder="Enter insurance ID"
                      disabled={isSubmitting}
                      className={classes.formInput}
                    />
                  </div>
                </div>

                <div className={classes.footer}>
                  <div className={classes.note}>
                    Insurance information is optional but recommended.
                  </div>
                  <div className={classes.footerButtons}>
                    <button 
                      className={classes.reset} 
                      onClick={() => goToPreviousTab("insurance")} 
                      disabled={isSubmitting}
                    >
                      <FaArrowLeft /> Previous
                    </button>
                    <button 
                      className={classes.next} 
                      onClick={() => goToNextTab("insurance")}
                      disabled={isSubmitting}
                    >
                      Next <FaArrowRight />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* DOCUMENTS TAB */}
            {activeTab === "documents" && (
              <div>
                <h3>Patient Documents</h3>
                <p className={classes.docSubtitle}>Upload and manage patient documents (Optional)</p>

                {uploadError && (
                  <div className={classes.submitError}>
                    <span>{uploadError}</span>
                  </div>
                )}
                {uploadSuccessMsg && (
                  <div className={classes.successMessage}>
                    <span>{uploadSuccessMsg}</span>
                  </div>
                )}

                <div className={classes.uploadSection}>
                  <h4>Upload New Document</h4>
                  <div className={classes.grid3}>
                    <div className={classes.inputGroup}>
                      <label>Document Name <span className={classes.required}>*</span></label>
                      <input
                        type="text"
                        name="document_name"
                        placeholder="Enter document name"
                        className={classes.formInput}
                        value={documentForm.document_name}
                        onChange={handleDocumentChange}
                        disabled={isUploading}
                      />
                    </div>
                    <div className={classes.inputGroup}>
                      <label>Document Type <span className={classes.required}>*</span></label>
                      <select
                        name="document_type"
                        className={classes.formSelect}
                        value={documentForm.document_type}
                        onChange={handleDocumentChange}
                        disabled={isUploading}
                      >
                        <option value="">Select document type</option>
                        {documentTypeOptions.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div className={classes.inputGroup}>
                      <label>Select Files <span className={classes.required}>*</span></label>
                      <input
                        type="file"
                        name="documents"
                        className={classes.fileInput}
                        onChange={handleDocumentChange}
                        multiple
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        disabled={isUploading}
                      />
                    </div>
                  </div>

                  {documentForm.documents.length > 0 && (
                    <div className={classes.fileList}>
                      {documentForm.documents.map((file, index) => (
                        <div key={index} className={classes.fileItem}>
                          <span className={classes.fileName}>
                            {getFileIcon(file.name)} {file.name}
                          </span>
                          <span className={classes.fileSize}>
                            {formatFileSize(file.size)}
                          </span>
                          <button
                            type="button"
                            className={classes.removeFileBtn}
                            onClick={() => handleRemoveDocument(index)}
                            disabled={isUploading}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <button 
                    className={classes.uploadDocsBtn} 
                    onClick={handleUploadDocuments}
                    disabled={isUploading}
                  >
                    {isUploading ? "Uploading..." : <><FaUpload /> Upload Documents</>}
                  </button>
                </div>

                {uploadedDocuments.length > 0 && (
                  <div className={classes.uploadedSection}>
                    <h4>Uploaded Documents</h4>
                    <div className={classes.uploadedList}>
                      {uploadedDocuments.map((doc, index) => (
                        <div key={index} className={classes.uploadedItem}>
                          <div className={classes.uploadedIcon}>
                            {getFileIcon(doc.file_name)}
                          </div>
                          <div className={classes.uploadedInfo}>
                            <div className={classes.uploadedName}>{doc.document_name}</div>
                            <div className={classes.uploadedType}>{doc.document_type}</div>
                            <div className={classes.uploadedDate}>
                              {doc.created_at && new Date(doc.created_at).toLocaleDateString()}
                            </div>
                          </div>
                          <div className={classes.uploadedActions}>
                            {doc.file_url && (
                              <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className={classes.viewBtn}>
                                View
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className={classes.footer}>
                  <div className={classes.note}>
                    Upload patient documents such as medical reports, prescriptions, and lab results.
                  </div>
                  <div className={classes.footerButtons}>
                    <button 
                      className={classes.reset} 
                      onClick={() => goToPreviousTab("documents")} 
                      disabled={isSubmitting}
                    >
                      <FaArrowLeft /> Previous
                    </button>
                    <button 
                      className={classes.createBtn} 
                      onClick={handleCreatePatient} 
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        "Creating..."
                      ) : (
                        <>
                          <FaSave /> Create Patient
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPatientPage;